import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HeartPulse, CreditCard, CheckCircle2, Activity, Calendar, Smile, Star } from "lucide-react";
import { Card, Btn, Badge, EmptyState, statusTone } from "@/components/ui-kit";
import { PainSparkline } from "@/pages/Pro";
import { PortalPWA } from "@/components/PortalPWA";
import { sessionsLabel } from "@/components/Cycle";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function PatientHome() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [paying, setPaying] = useState(null);
  const [checkin, setCheckin] = useState({});
  const [logging, setLogging] = useState(null);
  const [rate, setRate] = useState({});

  const load = useCallback(() => api.get("/patient/treatments").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const money = (n, cur = "₹") => `${cur}${Number(n || 0).toLocaleString("en-IN")}`;

  const pay = async (o) => {
    setPaying(o.id);
    try { await api.post(`/patient/orders/${o.id}/pay`); toast.success("Payment successful — thank you!"); load(); }
    catch { toast.error("Payment failed"); }
    setPaying(null);
  };
  const confirmSession = async (o, s) => {
    await api.post(`/patient/orders/${o.id}/schedule/${s.id}/confirm`);
    toast.success("Session confirmed"); load();
  };
  const logPain = async (o) => {
    const c = checkin[o.id] || {};
    if (c.score == null) return toast.error("Pick your pain level first");
    setLogging(o.id);
    try {
      await api.post(`/patient/orders/${o.id}/checkin`, { pain_score: Number(c.score), note: c.note || "" });
      toast.success("Thanks — your pain score was shared with your care team");
      setCheckin({ ...checkin, [o.id]: { score: null, note: "" } });
      load();
    } catch { toast.error("Could not log pain"); }
    setLogging(null);
  };

  const submitRating = async (o, sessionNo) => {
    const r = rate[o.id] || {};
    if (!r.stars) return toast.error("Tap a star rating first");
    try {
      await api.post(`/patient/orders/${o.id}/rate`, { stars: r.stars, comment: r.comment || "", session_no: sessionNo });
      toast.success("Thanks for rating your session");
      setRate({ ...rate, [o.id]: {} });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save rating"); }
  };

  const first = (user?.name || "there").split(" ")[0];

  return (
    <>
      <PortalPWA portal="patient" />
      <div className="mb-4">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Hi {first} 👋</h1>
        <p className="text-slate-500 text-[13.5px]">Your rehabilitation, tracked in one place.</p>
      </div>

      {items.length === 0 ? (
        <Card><EmptyState icon={<HeartPulse size={24} />} title="No treatments yet"
          subtitle="When your doctor places a treatment order for you, it will appear here." /></Card>
      ) : (
        <div className="space-y-4">
          {items.map((o) => {
            const oc = o.outcome || {}; const b = o.billing || {};
            const paid = b.payment_status === "Paid";
            const c = checkin[o.id] || {};
            const trend = oc.pain_trend || [];
            const recent = (o.checkins || []).slice(-3).reverse();
            return (
              <Card key={o.id} className="p-5" data-testid={`treatment-${o.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-[15px]">{o.package?.name || o.diagnosis_label}</div>
                    <div className="text-[12px] text-slate-500">{o.ref_no} · {o.diagnosis_label}</div>
                  </div>
                  <Badge tone={statusTone(o.status)}>{oc.status || o.status}</Badge>
                </div>

                <div className="mt-3 bg-slate-50 rounded-xl p-3 text-[13px]">
                  <div className="flex items-center gap-2 text-slate-600"><Activity size={14} className="text-teal-600" /> Physiotherapist: <span className="font-semibold text-slate-800">{o.assigned_physio?.name || "Matching…"}</span></div>
                </div>

                <div className="flex items-center justify-between text-[12px] text-slate-500 mt-3 mb-1">
                  <span>{sessionsLabel(oc)}</span><span>{oc.progress || 0}%</span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-teal-600 transition-all" style={{ width: `${oc.progress || 0}%` }} /></div>

                {(() => {
                  const done = oc.sessions_completed || 0;
                  const rated = o.rated_sessions || [];
                  if (!o.assigned_physio || done < 1 || rated.includes(done)) return null;
                  const r = rate[o.id] || {};
                  return (
                    <div className="mt-4 rounded-xl bg-amber-50/70 border border-amber-100 p-3" data-testid={`rate-session-${o.id}`}>
                      <div className="text-[12.5px] font-semibold text-slate-700 mb-1.5">How was session {done} with {o.assigned_physio.name}?</div>
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} onClick={() => setRate({ ...rate, [o.id]: { ...r, stars: n } })} data-testid={`rate-star-${o.id}-${n}`} aria-label={`${n} star`}>
                            <Star size={24} className={n <= (r.stars || 0) ? "text-amber-500" : "text-slate-300"} fill={n <= (r.stars || 0) ? "currentColor" : "none"} />
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input value={r.comment || ""} onChange={(e) => setRate({ ...rate, [o.id]: { ...r, comment: e.target.value } })} placeholder="Anything to share? (optional)" className="pq-input py-1.5 flex-1" data-testid={`rate-comment-${o.id}`} />
                        <Btn size="sm" variant="primary" onClick={() => submitRating(o, done)} data-testid={`rate-submit-${o.id}`}>Rate</Btn>
                      </div>
                    </div>
                  );
                })()}

                {/* Pain / progress self-log */}
                <div className="mt-4 pt-3 border-t border-dashed border-slate-200" data-testid={`pain-log-${o.id}`}>
                  <div className="pq-label mb-1.5 flex items-center gap-1.5"><Smile size={13} className="text-teal-600" /> How's your pain today?</div>
                  <div className="flex items-center gap-1 flex-wrap mb-2">
                    {[0,1,2,3,4,5,6,7,8,9,10].map((n) => (
                      <button key={n} data-testid={`patient-pain-${o.id}-${n}`} onClick={() => setCheckin({ ...checkin, [o.id]: { ...c, score: n } })}
                        className={`w-7 h-7 rounded-md text-[12px] font-semibold transition-colors ${c.score === n ? "bg-[#1A3A6D] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input value={c.note || ""} onChange={(e) => setCheckin({ ...checkin, [o.id]: { ...c, note: e.target.value } })} placeholder="Add a note (optional)" className="pq-input py-1.5 flex-1" data-testid={`patient-pain-note-${o.id}`} />
                    <Btn size="sm" variant="primary" onClick={() => logPain(o)} disabled={logging === o.id} data-testid={`patient-pain-submit-${o.id}`}>{logging === o.id ? "…" : "Log"}</Btn>
                  </div>
                  {trend.length > 0 && <div className="mt-3"><PainSparkline data={trend} /></div>}
                  {recent.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {recent.map((k) => (
                        <div key={k.id} className="flex items-center gap-2 text-[11.5px] text-slate-500">
                          <span className={`w-2 h-2 rounded-full ${k.pain_score >= 7 ? "bg-rose-500" : k.pain_score >= 4 ? "bg-amber-500" : "bg-emerald-500"}`} />
                          <span className="font-semibold text-slate-700">{k.pain_score}/10</span>
                          <span>· {k.date}{k.by === "physio" ? " · by therapist" : ""}</span>
                          {k.note && <span className="truncate">— {k.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {(o.schedule || []).length > 0 && (
                  <div className="mt-3">
                    <div className="pq-label mb-1.5 flex items-center gap-1.5"><Calendar size={12} /> Upcoming sessions</div>
                    {o.schedule.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 text-[12.5px] bg-slate-50 rounded-lg px-3 py-2 mb-1">
                        <span className="font-semibold">{s.date}{s.time ? ` · ${s.time}` : ""}</span>
                        {s.status === "Confirmed"
                          ? <Badge tone="emerald" className="ml-auto"><CheckCircle2 size={11} /> Confirmed</Badge>
                          : <Btn size="sm" variant="primary" className="ml-auto py-1" onClick={() => confirmSession(o, s)} data-testid={`confirm-${s.id}`}>Confirm</Btn>}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-dashed border-slate-200">
                  <div>
                    <div className="text-[11px] text-slate-500">Treatment package</div>
                    <div className="metric font-extrabold text-lg">{money(b.amount, b.currency)}</div>
                    {b.breakdown && <div className="text-[11px] text-slate-400">{b.breakdown.sessions} sessions · {b.breakdown.rehab_type}{b.breakdown.home_visit_fee ? " · home visit" : ""}{b.breakdown.equipment_fee ? " · equipment" : ""}</div>}
                  </div>
                  {paid
                    ? <Badge tone="emerald"><CheckCircle2 size={13} /> Paid</Badge>
                    : <Btn variant="primary" onClick={() => pay(o)} disabled={paying === o.id} data-testid={`pay-${o.id}`}><CreditCard size={15} /> {paying === o.id ? "Processing…" : "Pay now"}</Btn>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
